import { Button, Card, CardContent, Link, Stack } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { getPass } from "@utils/api/pass";
import { humanizeDuration } from "@utils/humanizeDuration";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DisableAllInactivatedPasses } from "./actions/DisableAllInactivatedPasses";
import { ExportCSV } from "./actions/ExportCSV";

export function PassBatchDetails() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const columns = useMemo<GridColDef[]>(() => [
    { field: "id", headerName: "ID", width: 300 },
    { field: "redeemCode", headerName: "Redemption Code", width: 150 },
    { field: "validDuration", headerName: "Pass Duration", width: 120, valueFormatter: ({ value }) => humanizeDuration(value) },
    { field: "createTime", headerName: "Create Time", width: 170, valueFormatter: ({value}) => value && moment(value).format("YYYY-MM-DD HH:mm:ss") },
    { field: "redeemExpiryTime", headerName: "Redeem Expiry Time", width: 200, valueFormatter: ({value}) => value && moment(value).format("YYYY-MM-DD HH:mm:ss") },
    { field: "userId", headerName: "User ID", width: 80, renderCell: ({ value }) => <Link target="_blank" href={`/txn?userId=${value}`}>{value}</Link>},
    { field: "redeemTime", headerName: "Redeem Time", width: 170, valueFormatter: ({value}) => value && moment(value).format("YYYY-MM-DD HH:mm:ss") },
    { field: "expiryTime", headerName: "Expiry Time", width: 170, valueFormatter: ({value}) => value && moment(value).format("YYYY-MM-DD HH:mm:ss") },
    { field: "remarks", headerName: "Remarks", width: 300 },
  ], []);

  const paginationModel = useMemo<GridPaginationModel>(() => ({
    pageSize: Number(searchParams.get("size") || 10),
    page: Number(searchParams.get("page") || 0)
  }), [searchParams]);

  const sortModel = useMemo<GridSortModel>(() => {
    const sort = searchParams.get("sort");
    return _.chain(sort || "")
    .split(",")
    .map(item => {
      const [field, sort] = item.split("|");
      if (field && sort) {
        return { field, sort: sort as GridSortDirection };  
      }
    })
    .compact()
    .value();
  }, [searchParams]);

  const handleLoad = useCallback(() => {
    setLoading(true);
    getPass({
      passBatchId: id,
      sort: sortModel.map(({ field, sort }) => [field, sort].join("|")).join(",") || undefined,
      page: paginationModel.page,
      size: paginationModel.pageSize
    })
    .then((data) => {
      setData(data.rows);
      setCount(data.count);
    })
    .finally(() => {
      setLoading(false);
    })
  }, [id, sortModel, paginationModel]);

  const handleSortModelChange = useCallback((model:GridSortModel) => {
    const sortString = model.map(({ field, sort }) => [field, sort].join("|")).join(",");
    setSearchParams(prev => {
      prev.set("sort", sortString);
      return prev;
    });
  }, []);

  const handlePaginationModelChange = useCallback((model:GridPaginationModel) => {
    setSearchParams(prev => {
      prev.set("page", model.page.toString());
      prev.set("size", model.pageSize.toString());
      return prev;
    });
  }, []);

  useEffect(handleLoad, [handleLoad]);

  useEffect(() => {
    if (!searchParams.has("sort")) {
      setSearchParams(prev => {
        prev.set("sort", "redeemCode|asc");
        return prev;
      })
    }
  }, [searchParams]);
  
  return <Stack spacing={1}>
    {id && <Card variant="outlined">
      <CardContent>
        {/* <DisableAllInactivatedPasses passBatchId={id}/> */}
        <ExportCSV passBatchId={id}/>
      </CardContent>
    </Card>}
    <DataGrid
      loading={loading}
      autoHeight
      paginationMode={"server"}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      sortingMode="server"
      onSortModelChange={handleSortModelChange}
      rowCount={count}
      columns={columns}
      rows={data}
      disableColumnMenu
    />
  </Stack>
}